import { Header, LeftNavigation, MUIBreadcrumb, MUIMenu } from "@abb/arcadia-ui-react"
import {
  MenuItemsType,
  PreviouspagesType,
} from "@abb/arcadia-ui-react/build/components/breadcrumb/types"
import { NavBarItemType } from "@abb/arcadia-ui-react/build/components/left-navigation/types"
import { useCallback, useContext, useEffect, useRef, useState } from "react"
import { Outlet, useLocation, useNavigate } from "react-router-dom"
import { ToastContainer } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import abbLogo from "../../assets/logos/abb-logo.png"
import { useAccessRequests } from "../../hooks/useAccessRequests"
import { useRoleRequestsBadge } from "../../hooks/useRoleRequestsBadge"
import { useVisitedPages } from "../../hooks/useVisitedPages"
import { DiMaProgress } from "../components/DiMaProgress"
import { ThemeSwitch } from "../components/setTheme"
import { AppContext } from "../context/AppContext"
import { UserContext } from "../context/UserContext"
import {
  AccessLevel,
  DimaFeatureShortNameEnum,
  hasUserAccessToFeature,
} from "../models/Authorization"
import { UserInfo } from "../models/User"
import { copyToClipboard } from "../utils/copyToClipboard"
import { NotAuthorized } from "./not_authorized_page/NotAuthorized"

const AVATAR_LENGTH = 2

const getFirstUsernameLetters = (username: string) => {
  if (username.includes(".")) {
    const pcs = username.split(".")
    return (pcs[0][0] + pcs[1][0]).toUpperCase()
  }
  return username.substring(0, AVATAR_LENGTH).toUpperCase()
}

interface MenuEntry {
  path: string
  text: string
  iconName: string
  feature: string
  accessLevel: AccessLevel
  badge?: number
}

const settingsPath = "/settings"

const userHasRoleManagerReadAccess = (user: UserInfo | undefined) =>
  user?.authorizations.applications.find((app) => app.featuresAccesses["RM"]) != null

export function Layout() {
  const { badges, setBadges } = useContext(AppContext)
  const { user, isLoadingUser } = useContext(UserContext)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const openUserMenu = Boolean(anchorEl)

  const location = useLocation()
  const navigate = useNavigate()

  const { isLoadingAccessRequest, accessRequestsBadge } = useAccessRequests()
  const { isLoadingRoleRequestsBadge, roleRequestsBadge } = useRoleRequestsBadge()

  const [isNavOpen, setIsNavOpen] = useState(false)
  const badgesRef = useRef(badges)
  const visitedPages = useVisitedPages()

  const [accountAnchor, setAccountAnchor] = useState<Element | null>(null)

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const el = document.querySelector(".avatar.initials")
      if (el && !accountAnchor) {
        setAccountAnchor(el)
        observer.disconnect()
      }
    })

    observer.observe(document.body, { childList: true, subtree: true })

    return () => observer.disconnect()
  }, [accountAnchor])

  useEffect(() => {
    badgesRef.current = badges
  }, [badges])

  useEffect(() => {
    if (
      accessRequestsBadge !== undefined &&
      badgesRef.current.accessRequests !== accessRequestsBadge
    ) {
      setBadges({ ...badgesRef.current, accessRequests: accessRequestsBadge })
    }
  }, [accessRequestsBadge, setBadges])

  useEffect(() => {
    if (
      roleRequestsBadge !== undefined &&
      badgesRef.current.roleRequests !== roleRequestsBadge
    ) {
      setBadges({ ...badgesRef.current, roleRequests: roleRequestsBadge })
    }
  }, [roleRequestsBadge, setBadges])

  const menuItems: MenuEntry[] = [
    {
      path: "/applications",
      text: "Applications",
      iconName: "icon_abb_dias icon-abb_app-window",
      feature: DimaFeatureShortNameEnum.AM,
      accessLevel: AccessLevel.Read,
    },
    {
      path: "/notifications",
      text: "Notifications",
      iconName: "icon_abb_dias icon-abb_alert-bell-notification-1",
      feature: DimaFeatureShortNameEnum.NM,
      accessLevel: AccessLevel.Read,
    },
    {
      path: "/role-requests",
      text: "Role requests",
      iconName: "icon_abb_dias icon-abb_user-check",
      feature: DimaFeatureShortNameEnum.RR,
      accessLevel: AccessLevel.Read,
      badge: badges.roleRequests,
    },
    {
      path: "/devices",
      text: "Devices",
      iconName: "icon_abb_dias icon-abb_connection",
      feature: DimaFeatureShortNameEnum.DM,
      accessLevel: AccessLevel.Read,
    },
    {
      path: "/files",
      text: "Files",
      iconName: "icon_abb_dias icon-abb_folder",
      feature: DimaFeatureShortNameEnum.FM,
      accessLevel: AccessLevel.Read,
    },
    {
      path: "/factory-reports",
      text: "Factory reports",
      iconName: "icon_abb_dias icon-abb_reporting",
      feature: DimaFeatureShortNameEnum.FR,
      accessLevel: AccessLevel.Read,
    },
    {
      path: "/users",
      text: "Users",
      iconName: "icon_abb_dias icon-abb_user",
      feature: DimaFeatureShortNameEnum.RM,
      accessLevel: AccessLevel.Read,
      badge: badges.accessRequests,
    },
    {
      path: "/app-registrations",
      text: "App Registrations",
      iconName: "icon_abb_dias icon-abb_app-setting-copy",
      feature: DimaFeatureShortNameEnum.AR,
      accessLevel: AccessLevel.Read,
    },
    {
      path: "/supplierPortal",
      text: "Supplier Portal",
      iconName: "icon_abb_dias icon-abb_truck-vehical",
      feature: DimaFeatureShortNameEnum.SP,
      accessLevel: AccessLevel.Write,
    },
    {
      path: "/gateways",
      text: "Gateways",
      iconName: "icon_abb_dias icon-abb_data-network",
      feature: DimaFeatureShortNameEnum.GW,
      accessLevel: AccessLevel.Write,
    },
    {
      path: "/production",
      text: "Production",
      iconName: "icon_abb_dias icon-abb_equipment",
      feature: DimaFeatureShortNameEnum.PR,
      accessLevel: AccessLevel.Read,
    },
    {
      path: "/deprecated-devices",
      text: "Deprecated Devices",
      iconName: "icon_abb_dias icon-abb_cancel-circle",
      feature: DimaFeatureShortNameEnum.DD,
      accessLevel: AccessLevel.Read,
    },
    {
      path: "/feedbacks",
      text: "Feedbacks",
      iconName: "icon_abb_dias icon-abb_feedback",
      feature: DimaFeatureShortNameEnum.FB,
      accessLevel: AccessLevel.Read,
    },
    {
      path: "/issue-reports",
      text: "Issue Reports",
      iconName: "icon_abb_dias icon-abb_alert-message-laptop",
      feature: DimaFeatureShortNameEnum.IR,
      accessLevel: AccessLevel.Read,
    },
    {
      path: "/trainings",
      text: "Trainings",
      iconName: "icon_abb_dias icon-abb_online-certificate-art-honor",
      feature: DimaFeatureShortNameEnum.TM,
      accessLevel: AccessLevel.Read,
    },
  ]

  const searchablePages = user?.authorizations
    ? menuItems.filter((item) =>
        hasUserAccessToFeature(user.authorizations, item.feature, item.accessLevel)
      )
    : []

  const isDevEnv = globalThis.location.host !== "dima.abb.com"

  const handleClick = useCallback(() => {
    if (accountAnchor) {
      setAnchorEl(accountAnchor as HTMLElement)
    }
  }, [accountAnchor])

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const navigateToCurrentUserTab = useCallback(() => {
    if (user) {
      navigate(`/users/${user.id}`)
    }
  }, [navigate, user])

  const copyToClipboardJwt = useCallback(() => {
    const jwt = globalThis.localStorage.getItem("lastAccessToken")
    if (jwt) {
      copyToClipboard(jwt)
    }
  }, [])

  const handleHamburgerMenuClick = useCallback(() => {
    setIsNavOpen((p) => !p)
  }, [])

  const handleBreadcrumbClick = useCallback(
    (u: string | PreviouspagesType | MenuItemsType) => {
      if (typeof u === "string") {
        return navigate(u)
      }

      if ("url" in u && typeof u.url === "string") {
        return navigate(u.url)
      }

      return undefined
    },
    [navigate]
  )

  const handleMenuClick = useCallback(
    (item: MenuItemsType | MenuItemsType[]) => {
      const menuItem = Array.isArray(item) ? item[0] : item

      if (menuItem.title === "Get JWT Token") {
        copyToClipboardJwt()
        return
      }

      if (menuItem.title === "My Profile" && userHasRoleManagerReadAccess(user)) {
        navigateToCurrentUserTab()
        return
      }

      if (menuItem.title === "Logout") {
        navigate("/logout")
      }
    },
    [copyToClipboardJwt, navigate, navigateToCurrentUserTab, user]
  )

  const handleClickNavItem = useCallback(
    (mainitem: string) => {
      if (mainitem && location.pathname !== mainitem) {
        navigate(mainitem)
      }
    },
    [location.pathname, navigate]
  )

  const noop = useCallback(() => {}, [])

  if (!user || isLoadingUser || isLoadingAccessRequest || isLoadingRoleRequestsBadge) {
    return <DiMaProgress />
  }

  if (!user.hasAccessToDima || !user.authorizations) {
    return <NotAuthorized />
  }

  const userHasSettingsAccess = hasUserAccessToFeature(
    user.authorizations,
    DimaFeatureShortNameEnum.RR,
    AccessLevel.Read
  )

  const navItems: NavBarItemType[] = [
    { navText: "Home", icon: "icon_abb_dias icon-abb_home", path: "/", disabled: false },
    ...searchablePages.map((item) => ({
      navText: item.text,
      icon: `${item.iconName} icon_abb_dias nav-icon`,
      path: item.path,
      alert: item.badge && item.badge > 0 ? ("primary" as const) : undefined,
      disabled: false,
    })),
  ]

  const bottomItems: NavBarItemType[] = userHasSettingsAccess
    ? [
        {
          navText: "Settings",
          icon: "icon_abb_dias icon-abb_setting",
          path: settingsPath,
        },
      ]
    : []

  const previousPages = visitedPages.slice(0, -1).map((page, index) => ({
    label: page.label,
    url: page.url,
    icon: index === 0 ? "icon_abb_dias icon-abb_home" : undefined,
  }))

  const currentPage = visitedPages.length > 0 ? visitedPages.at(-1) : { label: "Home" }

  const rootPath = "/" + location.pathname.split("/").find(Boolean)
  const selectedItem = navItems.find((x) => x.path === rootPath)

  return (
    <>
      <Header
        id="main-header"
        applicationName="Digital ELSP Manager"
        subtitle="DiMa"
        hasHamburgerMenu
        onHamburgerMenuClick={handleHamburgerMenuClick}
        logoSrc={abbLogo}
        userAvatar={{ initials: getFirstUsernameLetters(user.username) }}
        onClickAvatar={handleClick}
        handleClickTab={noop}
        handleTabMenuClick={noop}
        breadcrumb={
          <MUIBreadcrumb
            currentPage={{ label: currentPage?.label ?? "" }}
            previousPages={previousPages}
            onClickBreadcrumb={handleBreadcrumbClick}
          />
        }
        additionalContent={
          <>
            <ThemeSwitch />

            <MUIMenu
              id="account-menu"
              anchorEl={accountAnchor as Element}
              open={openUserMenu}
              handleClose={handleClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              handleMenuClick={handleMenuClick}
              menuItems={[
                ...(isDevEnv
                  ? [
                      {
                        id: "get-jwt",
                        title: "Get JWT Token",
                        addDividerBelow: userHasRoleManagerReadAccess(user),
                      },
                      ...(userHasRoleManagerReadAccess(user)
                        ? [
                            {
                              id: "my-profile",
                              title: "My Profile",
                              addDividerBelow: true,
                            },
                          ]
                        : []),
                    ]
                  : []),
                { id: "logout", title: "Logout" },
              ]}
              size={32}
            />
          </>
        }
      />

      <div className="abb-main-row">
        <LeftNavigation
          id="main-left-navigation"
          isNavOpen={isNavOpen}
          navbarContents={navItems}
          navbarBottomContents={bottomItems}
          selectedItem={selectedItem}
          onClickNavItem={handleClickNavItem}
        />

        <div className="abb-content">
          <ToastContainer
            position="bottom-right"
            autoClose={5000}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnHover={false}
            pauseOnFocusLoss={false}
            draggable
            theme="colored"
          />
          <Outlet />
        </div>
      </div>
    </>
  )
}
